import { useAppSelector } from '@/hooks/hook';
import { BookingNotification, NotificationCount } from '@/interfaces/Notifcations';
import { POST } from '@/services/api';
import { API_ROUTES } from '@/services/environment';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SlBell } from "react-icons/sl";

const BellIcon = () => (
    <div className='p-2 bg-p-gray rounded-full'>

        <SlBell size={22} />
    </div>
);

const NotificationItem = ({ notification, onRead, roles }: { notification: BookingNotification, onRead: (id: string) => void, roles: string }) => {

    if (roles === 'admin') {
        return (
            <div className={`rounded-xl mb-1 p-4 border-b last:border-b-0 transition-colors duration-200 ${notification.admin_seen ? ' bg-white hover:bg-gray-50 ' : '  bg-gray-100'}`}>
                <p className="text-sm text-gray-800">{notification.title} <span className='text-[10px]'>{notification.type === 'subscription' && '🎉'}</span> </p>
                <p className="text-xs text-gray-500">{notification.description}</p>
                <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-primary-red">{new Date(notification.created_at).toLocaleString()}</span>
                    {!notification.admin_seen &&
                        <p
                            className="text-xs text-primary-red transition-colors duration-200"
                        >New</p>
                    }
                </div>
            </div>
        )
    } else {
        return (
            <div className={`rounded-xl mb-1 p-4 border-b last:border-b-0 transition-colors duration-200 ${notification.seen ? ' bg-white hover:bg-gray-50 ' : '  bg-gray-100'}`}>
                <p className="text-sm text-gray-800">{notification.title} <span className='text-[10px]'>{notification.type === 'subscription' && '🎉'}</span> </p>
                <p className="text-xs text-gray-500">{notification.description}</p>
                <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-primary-red">{new Date(notification.created_at).toLocaleString()}</span>
                    {!notification.seen &&
                        <p
                            className="text-xs text-primary-red transition-colors duration-200"
                        >New</p>
                    }
                </div>
            </div>
        )
    }
}

interface notificationInterFace {
    notificationList: BookingNotification[];
    notificationCount: NotificationCount | null | undefined;
    closeSidebar?: () => void;
    getNotificationsCount: () => void;
    getNotifications: () => void;
}

export default function NotificationBell({ notificationList, closeSidebar, notificationCount, getNotificationsCount, getNotifications }: notificationInterFace) {
    const {
        markAsReadNotificationApi,
    } = API_ROUTES;
    const { refreshToken, roles } = useAppSelector((store) => store.user);

    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<BookingNotification[]>(notificationList);
    const navigate = useNavigate()

    const dropdownRef = useRef<HTMLDivElement>(null);

    const unreadCount = notifications.filter(n => !n.seen).length;

    const handleToggle = () => {
        if (!isOpen) {
            getNotificationsCount();
            getNotifications();
        }
        setIsOpen(!isOpen)
    };

    const handleMarkAsRead = (id: string) => {
        if (roles === 'admin') {
            setNotifications(notifications.map(n =>
                n.id === id ? { ...n, admin_seen: true } : n
            ));
        } else {
            setNotifications(notifications.map(n =>
                n.id === id ? { ...n, seen: true } : n
            ));
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);

            }
        };
        setNotifications(notificationList)

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [notificationList.length]);

    const notiNavigate = (url: string, notificationId: string, type: 'subscription' | 'invoice' | 'Note' | 'booking' | 'quote' | 'cashflow' | 'reward_points') => {
        closeSidebar && closeSidebar()
        setIsOpen(false)
        markAsReadFun(notificationId)
        if (type === 'subscription') {
            navigate('/events?tab=Bookings', { state: { url } })
        } else if (type === 'Note' || type === 'booking') {
            navigate('/events?tab=Bookings', { state: { url } })
        } else if (type === 'quote') {
            navigate('/events?tab=Leads', { state: { url } })
        } else if (type === 'reward_points' || type === 'cashflow') {
            navigate('/reward', { state: { url } })
        } else {
            navigate('/billing')
        }
    }

    const markAsReadFun = async (notificationId: string) => {
        try {
            const res = await POST(
                `${markAsReadNotificationApi} `,
                {
                    notificationId
                },
                refreshToken
            )
            if (res.data.message) {
                if (roles === 'admin') {
                    setNotifications(notifications.map((item: BookingNotification) => {
                        if (item.id === notificationId) {
                            item.admin_seen = true
                        }
                        return item
                    }))
                } else {
                    setNotifications(notifications.map((item: BookingNotification) => {
                        if (item.id === notificationId) {
                            item.seen = true
                        }
                        return item
                    }))
                }
                getNotificationsCount()
            }
        } catch (error) {
            console.log('mark as read API error:', error)
        }
    }
    const notificationCountFlag = () => {
        const count = notificationCount?.count ?? 0; // Ensure count is always a number

        if (count > 99) {
            return "99+";
        } else if (count > 0) {
            return count;
        } else {
            return 0; // Or another default like ''
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none"
                onClick={handleToggle}
                aria-label={`Notifications ${notificationCount?.count ?? 0 > 0 ? `, ${notificationCount?.count} unread` : ''}`}
            >
                <BellIcon />
                {(notificationCount?.count ?? 0) > 0 && (
                    <span className={`absolute top-0 right-0 inline-flex items-center justify-center text-xs font-bold text-white bg-[#3acb61] rounded-full ${notificationCountFlag() === '99+' ? 'w-6 h-6' : 'w-5 h-5'}`}>
                        {notificationCountFlag()}
                    </span>
                )}
            </button>
            {isOpen && (
                <div className="p-3 rounded-3xl fixed inset-x-4 top-20 lg:absolute lg:inset-x-auto lg:top-full lg:right-0 lg:left-auto lg:mt-2 lg:w-80 bg-white shadow-lg overflow-hidden z-50 border border-gray-200 transform origin-top-right transition-all duration-200 ease-out">
                    <div className="p-4 bg-gradient-to-r ">
                        <h2 className="text-lg font-semibold ">Notifications</h2>
                    </div>
                    <div className="max-h-[60vh] lg:max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                            notifications.map(notification => (
                                <div key={notification.id}
                                    onClick={() => notiNavigate(notification.action, notification.id, notification.type)}
                                    className='cursor-pointer'
                                >
                                    <NotificationItem
                                        notification={notification}
                                        onRead={handleMarkAsRead}
                                        roles={roles}
                                    />
                                </div>
                            ))
                        ) : (
                            <p className="p-4 text-sm text-gray-500">No notifications</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
